import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useImpersonation from '../../hooks/useImpersonation';
import {
  MdReceiptLong, MdAdd, MdClose, MdCheck, MdCheckCircle, MdCancel,
  MdAttachFile, MdFilterList, MdSearch, MdOpenInNew,
  MdDeleteOutline, MdEdit, MdExpandMore, MdExpandLess,
  MdTrendingDown, MdPending, MdDownload, MdInfoOutline,
  MdLock, MdWarning, MdBlock, MdTableChart, MdAutorenew,
} from 'react-icons/md';
import { FaReceipt } from 'react-icons/fa';
import * as expSvc from '../../services/expenseService';
import { downloadExpenseReport } from '../../services/reportExportService';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import useAuth from '../../hooks/useAuth';
import { adToBs, BS_MONTHS } from '../../utils/calendar';
import DualDatePicker from '../../components/common/DualDatePicker';
import DualDate from '../../components/common/DualDate';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CATEGORIES = [
  { value: 'rent',             label: 'Rent' },
  { value: 'electricity',      label: 'Electricity' },
  { value: 'water',            label: 'Water' },
  { value: 'internet',         label: 'Internet' },
  { value: 'equipment',        label: 'Equipment' },
  { value: 'trainer_payment',  label: 'Trainer Payment' },
  { value: 'cleaning',         label: 'Cleaning' },
  { value: 'transport',        label: 'Transport' },
  { value: 'tournament',       label: 'Tournament' },
  { value: 'repair_maintenance', label: 'Repair and Maintenance' },
  { value: 'marketing',        label: 'Marketing' },
  { value: 'office_supplies',  label: 'Office Supplies' },
  { value: 'software',         label: 'Software' },
  { value: 'other',            label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: 'cash',         label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'khalti',       label: 'Khalti' },
  { value: 'esewa',        label: 'eSewa' },
  { value: 'fonepay_qr',   label: 'Fonepay QR' },
  { value: 'connectips',   label: 'ConnectIPS' },
  { value: 'other',        label: 'Other' },
];

const STATUS_STYLES = {
  pending:   { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/20',   label: 'Awaiting Admin Approval' },
  approved:  { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20', label: 'Approved'                },
  rejected:  { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/20',     label: 'Rejected'                },
  draft:     { bg: 'bg-nav-hover',      text: 'text-muted',       border: 'border-divider',        label: 'Draft'                   },
  archived:  { bg: 'bg-nav-hover',      text: 'text-muted',       border: 'border-divider',        label: 'Archived'                },
  cancelled: { bg: 'bg-nav-hover',      text: 'text-muted',       border: 'border-divider',        label: 'Cancelled'               },
};

const catLabel    = (v) => CATEGORIES.find(c => c.value === v)?.label || v;
const methodLabel = (v) => PAYMENT_METHODS.find(m => m.value === v)?.label || v;
const fmtNPR      = (n) => `NPR ${Number(n || 0).toLocaleString()}`;
const pad2        = (n) => String(n).padStart(2, '0');
// Kathmandu-local "now" (UTC+5:45) read via getUTC* — same pattern as FinanceOverview;
// builds padded Y/M/D strings without toISOString, so no UTC month/day off-by-one.
const kthNow      = () => new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
const kthMonthStr = () => { const k = kthNow(); return `${k.getUTCFullYear()}-${pad2(k.getUTCMonth() + 1)}`; };
const kthDateStr  = () => { const k = kthNow(); return `${k.getUTCFullYear()}-${pad2(k.getUTCMonth() + 1)}-${pad2(k.getUTCDate())}`; };
const THIS_MONTH  = kthMonthStr();

// Status filter options (same values/labels as the previous native <select>).
const STATUS_OPTIONS = [
  { value: '',          label: 'All Statuses' },
  { value: 'pending',   label: 'Awaiting Admin Approval' },
  { value: 'approved',  label: 'Approved' },
  { value: 'rejected',  label: 'Rejected' },
  { value: 'draft',     label: 'Draft' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ── FilterSelect — custom dark dropdown matching the month picker ──────────────
// options: [{ value, label }] (the first/"All …" option carries value: '').
function FilterSelect({ value, onChange, options, className = '' }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selected = options.find(o => o.value === value) || options[0];

  const openMenu = () => {
    const r = rootRef.current?.getBoundingClientRect();
    if (r) setAlignRight(r.left + 224 > window.innerWidth - 8); // 224 = w-56; flip near the edge
    setOpen(true);
  };

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="h-10 w-full flex items-center justify-between gap-2 px-3 rounded-lg border border-divider bg-card text-sm text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
      >
        <span className="truncate">{selected?.label}</span>
        <MdExpandMore className={`text-muted text-lg flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute top-full mt-1 z-50 w-56 max-w-[calc(100vw-1rem)] bg-card border border-divider rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto ${alignRight ? 'right-0' : 'left-0'}`}
        >
          {options.map(o => {
            const isSel = o.value === value;
            return (
              <button
                key={o.value || '__all__'}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm rounded-md cursor-pointer transition-colors hover:bg-nav-hover ${
                  isSel ? 'text-primary font-medium' : 'text-secondary'
                }`}
              >
                <span className="truncate">{o.label}</span>
                {isSel && <MdCheck className="text-primary text-base flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
}

// Recurring markers: the blue badge flags the template; the gray badge flags an
// auto-generated monthly copy.
function RecurringBadge({ expense }) {
  if (expense.isRecurring) {
    return (
      <span
        title="Recurring template — a copy is auto-generated every month."
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-100">
        <MdAutorenew className="text-[11px]" /> Recurring template
      </span>
    );
  }
  if (expense.recurringParentId) {
    return (
      <span
        title="Generated from a recurring template. Edit this row to change just this month."
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-nav-hover text-muted border border-divider">
        <MdAutorenew className="text-[11px]" /> Auto-generated
      </span>
    );
  }
  return null;
}

// Label of the month AFTER the current one, in the club's calendar (for the recurring
// help text). e.g. "Shrawan 2083" (BS) or "July 2026" (AD).
function nextMonthLabel(calendar) {
  if (calendar === 'bs') {
    const bs = adToBs(new Date());
    const m = bs.month === 12 ? 1 : bs.month + 1;
    const y = bs.month === 12 ? bs.year + 1 : bs.year;
    return `${BS_MONTHS[m - 1]} ${y}`;
  }
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return next.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

// ── Attachment preview ────────────────────────────────────────────────────────
function AttachmentThumb({ att, expenseId }) {
  const isPdf     = att.mimeType === 'application/pdf' || att.url?.endsWith('.pdf');
  const isLegacy  = !att.url?.startsWith('http');
  const [loading, setLoading] = useState(null); // 'view' | 'download' | null

  const openBlob = async (mode) => {
    setLoading(mode);
    try {
      const res  = await expSvc.viewAttachment(expenseId, att._id, mode === 'download');
      const blob = new Blob([res.data], { type: att.mimeType || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = att.originalName || att.filename || 'attachment';
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      } else {
        const tab = window.open('', '_blank');
        if (tab) {
          tab.location.href = blobUrl;
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
          toast.error('Popup blocked. Please allow popups for this site.');
          URL.revokeObjectURL(blobUrl);
        }
      }
    } catch {
      toast.error('Failed to load attachment.');
    } finally {
      setLoading(null);
    }
  };

  if (isLegacy) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed text-xs font-medium"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted)' }}
        title="This receipt was stored on the old server and may no longer be accessible">
        <MdWarning className="flex-shrink-0 text-amber-400" />
        <span className="truncate max-w-[120px]">{att.originalName || att.filename}</span>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-dashed text-xs font-medium overflow-hidden"
        style={{ borderColor: 'var(--color-border)' }}>
        <button onClick={() => openBlob('view')} disabled={!!loading}
          className="flex items-center gap-1 px-2.5 py-1.5 hover:bg-nav-hover transition-colors disabled:opacity-50"
          style={{ color: 'var(--color-muted)' }}>
          {loading === 'view' ? <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" /> : <MdAttachFile className="flex-shrink-0" />}
          <span className="truncate max-w-[80px]">{att.originalName || att.filename}</span>
        </button>
        <button onClick={() => openBlob('download')} disabled={!!loading}
          className="px-2 py-1.5 hover:bg-nav-hover transition-colors disabled:opacity-50 border-l"
          style={{ color: 'var(--color-muted)', borderColor: 'var(--color-border)' }}
          title="Download">
          {loading === 'download' ? <span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" /> : <MdDownload className="text-sm" />}
        </button>
      </div>
    );
  }

  const imageUrl = att.url?.startsWith('http') ? att.url : `${API_BASE}${att.url?.startsWith('/') ? '' : '/'}${att.url}`;
  return (
    <a href={imageUrl} target="_blank" rel="noreferrer"
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed text-xs font-medium transition-colors hover:border-primary/40"
      style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted)' }}>
      <MdOpenInNew className="flex-shrink-0 text-[10px]" />
      <span className="truncate max-w-[120px]">{att.originalName || att.filename}</span>
    </a>
  );
}

// ── Expense Form Modal ────────────────────────────────────────────────────────
function ExpenseFormModal({ expense, onClose, onSaved }) {
  const isEdit = !!expense?._id;
  const currentTheme = useTheme()?.currentTheme;
  const { currentCalendar } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const fileRef = useRef();
  const [files, setFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [form, setForm] = useState({
    title:         expense?.title         || '',
    amount:        expense?.amount        || '',
    expenseDate:   expense?.expenseDate   ? expense.expenseDate.slice(0, 10) : kthDateStr(),
    category:      expense?.category      || '',
    paymentMethod: expense?.paymentMethod || '',
    vendorName:    expense?.vendorName    || '',
    receiptNumber: expense?.receiptNumber || '',
    description:   expense?.description  || '',
    notes:         expense?.notes        || '',
  });
  const [isRecurring, setIsRecurring] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter(f => {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is too large (max 5 MB)`); return false; }
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowed.includes(f.type)) { toast.error(`${f.name}: only JPEG, PNG, WEBP, PDF allowed`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
    setPreviewFiles(prev => [...prev, ...valid.map(f => ({ file: f, url: URL.createObjectURL(f) }))]);
  };

  const removeFile = (i) => {
    setFiles(prev => prev.filter((_, idx) => idx !== i));
    setPreviewFiles(prev => {
      URL.revokeObjectURL(prev[i]?.url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.paymentMethod || !form.amount || !form.expenseDate) {
      return toast.error('Please fill all required fields');
    }
    if (Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');

    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (!isEdit && isRecurring) fd.append('isRecurring', 'true');
      files.forEach(f => fd.append('attachments', f));

      if (isEdit) {
        await expSvc.updateExpense(expense._id, fd);
        toast.success('Expense updated');
      } else {
        await expSvc.createExpense(fd);
        toast.success('Expense created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors bg-nav-hover/40 border-divider focus:border-primary focus:ring-2 focus:ring-primary/30 text-secondary placeholder:text-subtle';
  const lbl = 'block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-xl bg-card sm:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <FaReceipt className="text-primary" />
            {isEdit ? 'Edit Expense' : 'Add Expense'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted hover:text-muted transition-colors">
            <MdClose className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-5 space-y-4">
          {/* Title */}
          <div>
            <label className={lbl}>Title <span className="text-red-500">*</span></label>
            <input className={inp} value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. Monthly rent - June" required />
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Amount (NPR) <span className="text-red-500">*</span></label>
              <input className={inp} type="number" min="1" step="0.01" value={form.amount}
                onChange={e => set('amount', e.target.value)} placeholder="0" required />
            </div>
            <div>
              <label className={lbl}>Date <span className="text-red-500">*</span></label>
              <DualDatePicker value={form.expenseDate} onChange={v => set('expenseDate', v)} />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className={lbl}>Category <span className="text-red-500">*</span></label>
            <select className={inp} value={form.category} onChange={e => set('category', e.target.value)} required>
              <option value="">Select category</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* Payment Method */}
          <div>
            <label className={lbl}>Payment Method <span className="text-red-500">*</span></label>
            <select className={inp} value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)} required>
              <option value="">Select method</option>
              {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          {/* Vendor + Receipt */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Vendor Name</label>
              <input className={inp} value={form.vendorName} onChange={e => set('vendorName', e.target.value)}
                placeholder="e.g. Nepal Telecom" />
            </div>
            <div>
              <label className={lbl}>Receipt Number</label>
              <input className={inp} value={form.receiptNumber} onChange={e => set('receiptNumber', e.target.value)}
                placeholder="e.g. INV-2025-001" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={lbl}>Description</label>
            <textarea className={`${inp} resize-none`} rows={2} value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Brief description of this expense" />
          </div>

          {/* Notes */}
          <div>
            <label className={lbl}>Internal Notes</label>
            <textarea className={`${inp} resize-none`} rows={2} value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any additional notes for your records" />
          </div>

          {/* Recurring (create only) */}
          {!isEdit && (
            <div className="rounded-xl border border-divider p-3.5">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={e => setIsRecurring(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-divider text-primary focus:ring-primary/30"
                />
                <span>
                  <span className="block text-sm font-semibold text-secondary">Make this a monthly recurring expense</span>
                  <span className="block text-xs text-muted mt-0.5 leading-relaxed">
                    This expense will automatically appear every month going forward. You can edit each
                    month's amount individually, or stop the recurrence at any time.
                  </span>
                </span>
              </label>
              {isRecurring && (
                <p className="text-xs text-primary/80 mt-2 pl-7 leading-relaxed">
                  Next month's entry will be created automatically on the 1st of{' '}
                  <strong>{nextMonthLabel(currentCalendar)}</strong> based on your club's calendar.
                </p>
              )}
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className={lbl}>Receipt / Attachments</label>
            <div
              className="border-2 border-dashed border-divider rounded-xl p-4 text-center cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => fileRef.current?.click()}>
              <MdAttachFile className="mx-auto text-2xl text-subtle mb-1" />
              <p className="text-xs text-muted">Tap to upload receipt or photo</p>
              <p className="text-[10px] text-subtle mt-0.5">JPEG, PNG, WEBP, PDF — max 5 MB each</p>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
              capture="environment"
              onChange={handleFiles}
              className="hidden"
            />

            {/* Preview new files */}
            {previewFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {previewFiles.map((pf, i) => (
                  <div key={i} className="relative group">
                    {pf.file.type.startsWith('image/') ? (
                      <img src={pf.url} alt="" className="w-16 h-16 object-cover rounded-lg border border-divider" />
                    ) : (
                      <div className="w-16 h-16 flex items-center justify-center rounded-lg border border-divider bg-nav-hover">
                        <MdAttachFile className="text-muted text-xl" />
                      </div>
                    )}
                    <button type="button" onClick={() => removeFile(i)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-secondary text-card rounded-full flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                      <MdClose className="text-[10px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing attachments */}
            {isEdit && expense?.attachments?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-muted mb-1.5">Existing attachments:</p>
                <div className="flex flex-wrap gap-2">
                  {expense.attachments.map(att => <AttachmentThumb key={att._id} att={att} expenseId={expense._id} />)}
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
              Cancel
            </button>
            <button type="submit" disabled={submitting || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className={`flex-1 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed`}>
              {submitting ? 'Saving...' : isEdit ? 'Update Expense' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────
function RejectModal({ expense, onClose, onRejected }) {
  const { isReadOnly } = useImpersonation();
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => expSvc.rejectExpense(expense._id, reason),
    onSuccess:  () => { toast.success('Expense rejected'); onRejected(); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-card rounded-2xl shadow-2xl p-6">
        <h2 className="font-bold text-secondary mb-1">Reject Expense</h2>
        <p className="text-sm text-muted mb-4">
          You are rejecting: <strong className="text-secondary">{expense.title}</strong>
        </p>
        <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">
          Reason for rejection <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Explain why this expense is rejected..."
          className="w-full px-3 py-2 rounded-lg border border-divider bg-nav-hover/40 text-secondary placeholder:text-subtle text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none"
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-divider text-sm font-medium text-muted hover:bg-nav-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={() => reason.trim() && mutation.mutate()}
            disabled={!reason.trim() || mutation.isPending || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {mutation.isPending ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Expense Detail Drawer ─────────────────────────────────────────────────────
function ExpenseDetail({ id, onClose, onApprove, onReject, onEdit, onArchive, onCancel, onStopRecurring, isAdmin, isPrimaryAdmin }) {
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const { data, isPending } = useQuery({
    queryKey: ['expense-detail', id],
    queryFn:  () => expSvc.getExpense(id).then(r => r.data),
  });
  const expense = data?.expense;
  const [historyOpen,   setHistoryOpen]   = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmStop,   setConfirmStop]   = useState(false);

  if (isPending) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-card rounded-2xl p-8 animate-pulse w-80 h-60" />
      </div>
    );
  }
  if (!expense) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-lg bg-card sm:rounded-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider">
          <h2 className="font-bold text-secondary truncate pr-4">{expense.title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-nav-hover text-muted flex-shrink-0 transition-colors">
            <MdClose className="text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5 space-y-5">
          {/* Status + amount */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-2xl font-extrabold text-secondary">{fmtNPR(expense.amount)}</p>
              <p className="text-xs text-muted mt-0.5"><DualDate date={expense.expenseDate} /></p>
            </div>
            <StatusBadge status={expense.status} />
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['Category',       catLabel(expense.category)],
              ['Payment Method', methodLabel(expense.paymentMethod)],
              ['Vendor',         expense.vendorName || '—'],
              ['Receipt No.',    expense.receiptNumber || '—'],
              ['Submitted by',   expense.submittedBy?.name || '—'],
              ['Role',           expense.submittedByRole || '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-muted mb-0.5">{label}</p>
                <p className="font-medium text-secondary capitalize">{value}</p>
              </div>
            ))}
          </div>

          {/* Recurring template / copy info */}
          {expense.isRecurring && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
              <MdAutorenew className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-blue-400">
                  Recurring template {expense.recurringActive ? '(active)' : '(stopped)'}
                </p>
                <p className="text-xs text-blue-400 mt-0.5">
                  {expense.recurringActive
                    ? 'A copy of this expense is generated automatically every month.'
                    : 'Generation is stopped. Existing monthly copies remain unaffected.'}
                </p>
              </div>
            </div>
          )}
          {expense.recurringParentId && (
            <div className="bg-nav-hover border border-divider rounded-xl px-4 py-3 flex items-start gap-2">
              <MdAutorenew className="text-muted flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted">
                Auto-generated from a recurring template. Editing this entry changes only this month.
              </p>
            </div>
          )}

          {expense.description && (
            <div>
              <p className="text-xs text-muted mb-1">Description</p>
              <p className="text-sm text-secondary">{expense.description}</p>
            </div>
          )}

          {expense.notes && (
            <div>
              <p className="text-xs text-muted mb-1">Notes</p>
              <p className="text-sm text-secondary">{expense.notes}</p>
            </div>
          )}

          {/* Rejection reason */}
          {expense.status === 'rejected' && expense.rejectionReason && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-red-400 mb-0.5">Rejection Reason</p>
              <p className="text-sm text-red-400">{expense.rejectionReason}</p>
              <p className="text-xs text-red-400/70 mt-1">
                Rejected by {expense.rejectedBy?.name || '—'} on <DualDate date={expense.rejectedAt} />
              </p>
            </div>
          )}

          {/* Approved info */}
          {expense.status === 'approved' && expense.approvedBy && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-emerald-400 mb-0.5">Approved</p>
              <p className="text-xs text-emerald-400">
                By {expense.approvedBy?.name || '—'} on <DualDate date={expense.approvedAt} />
              </p>
            </div>
          )}

          {/* Receipt missing warning */}
          {expense.status === 'pending' && (expense.attachments?.length || 0) === 0 && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
              <MdWarning className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-400">No receipt attached</p>
                <p className="text-xs text-amber-400/80 mt-0.5">This expense has no supporting document. Consider asking the trainer to add one before approving.</p>
              </div>
            </div>
          )}

          {/* Approved lock notice */}
          {expense.status === 'approved' && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 flex items-start gap-2">
              <MdLock className="text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-emerald-400">Official Financial Record</p>
                <p className="text-xs text-emerald-400/80 mt-0.5">Locked for transparency. This approved expense cannot be edited, archived, or cancelled.</p>
              </div>
            </div>
          )}

          {/* Attachments */}
          {expense.attachments?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Attachments</p>
              <div className="flex flex-wrap gap-2">
                {expense.attachments.map(att => <AttachmentThumb key={att._id} att={att} expenseId={expense._id} />)}
              </div>
            </div>
          )}

          {/* Edit history */}
          {expense.editHistory?.length > 0 && (
            <div>
              <button
                onClick={() => setHistoryOpen(h => !h)}
                className="flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-muted transition-colors">
                {historyOpen ? <MdExpandLess /> : <MdExpandMore />}
                Edit History ({expense.editHistory.length})
              </button>
              {historyOpen && (
                <div className="mt-2 space-y-2">
                  {expense.editHistory.map((h, i) => (
                    <div key={i} className="text-xs bg-nav-hover rounded-lg px-3 py-2">
                      <span className="font-medium text-secondary">{h.editedBy?.name || 'Unknown'}</span>
                      <span className="text-muted"> — <DualDate date={h.editedAt} /></span>
                      <div className="text-muted mt-0.5">
                        {Object.entries(h.changes || {}).map(([k, v]) => (
                          <div key={k}>{k}: {JSON.stringify(v.from)} → {JSON.stringify(v.to)}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {isAdmin && (
          <div className="px-5 py-4 border-t border-divider flex flex-wrap gap-2">
            {expense.status === 'pending' && (
              <>
                <button onClick={() => !isReadOnly && onApprove(expense)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <MdCheckCircle /> Approve
                </button>
                <button onClick={() => !isReadOnly && onReject(expense)}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  <MdCancel /> Reject
                </button>
              </>
            )}
            {['pending', 'draft', 'rejected'].includes(expense.status) && (
              <button onClick={() => onEdit(expense)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <MdEdit /> Edit
              </button>
            )}
            {!['approved', 'cancelled', 'archived'].includes(expense.status) && !confirmCancel && (
              <button onClick={() => setConfirmCancel(true)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <MdBlock /> Cancel
              </button>
            )}
            {!['approved', 'cancelled', 'archived'].includes(expense.status) && (
              <button onClick={() => onArchive(expense)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:bg-nav-hover transition-colors ml-auto disabled:opacity-50 disabled:cursor-not-allowed">
                <MdDeleteOutline /> Archive
              </button>
            )}
            {confirmCancel && (
              <div className="w-full flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
                <p className="text-xs text-red-400 flex-1">Cancel this expense submission?</p>
                <button onClick={() => { onCancel(expense); setConfirmCancel(false); }}
                  disabled={isReadOnly}
                  title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Confirm
                </button>
                <button onClick={() => setConfirmCancel(false)}
                  className="text-xs text-muted hover:text-secondary px-3 py-1.5 rounded-lg border border-divider transition-colors">
                  No
                </button>
              </div>
            )}

            {/* Stop recurring — Primary Admin only, active templates only */}
            {isPrimaryAdmin && expense.isRecurring && expense.recurringActive && !confirmStop && (
              <button onClick={() => setConfirmStop(true)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-divider text-xs font-semibold text-muted hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/10 transition-colors w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                <MdAutorenew /> Stop future months
              </button>
            )}
            {confirmStop && (
              <div className="w-full bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-3">
                <p className="text-xs text-amber-400 mb-1 font-semibold">Stop generating monthly copies for this expense?</p>
                <p className="text-xs text-amber-400/80 mb-2.5">
                  Already-created entries will remain. This action cannot be undone in v1 — to restart,
                  you'll need to create a new recurring expense.
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => { onStopRecurring(expense); setConfirmStop(false); }}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                    className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Stop future months
                  </button>
                  <button onClick={() => setConfirmStop(false)}
                    className="text-xs text-muted hover:text-secondary px-3 py-1.5 rounded-lg border border-divider transition-colors">
                    Keep recurring
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Summary Cards ─────────────────────────────────────────────────────────────
function SummaryCards({ summary }) {
  if (!summary) return null;
  const cards = [
    { label: 'This Month',  value: fmtNPR(summary.thisMonthTotal), sub: `${summary.thisMonthCount} expenses`,  icon: MdReceiptLong, color: 'text-muted' },
    { label: 'This Year',   value: fmtNPR(summary.thisYearTotal),  sub: 'approved expenses',                   icon: MdTrendingDown, color: 'text-muted' },
    { label: 'Awaiting Approval', value: summary.pendingCount,   sub: 'trainer expenses to review', icon: MdPending,     color: 'text-amber-400' },
    { label: 'Approved',         value: summary.approvedCount,  sub: 'this cycle',                 icon: MdCheckCircle, color: 'text-emerald-400' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(({ label, value, sub, icon: Icon, color }) => (
        <div key={label} className="bg-card rounded-2xl border border-divider p-4 shadow-sm card-hover">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-muted">{label}</p>
            <Icon className={`text-xl ${color} opacity-70`} />
          </div>
          <p className="text-[28px] leading-none font-medium text-secondary">{value}</p>
          <p className="text-xs text-muted mt-1.5">{sub}</p>
        </div>
      ))}
    </div>
  );
}

// ── Expenses Panel Content ────────────────────────────────────────────────────
export function ExpensesPanel() {
  const qc = useQueryClient();
  const currentTheme = useTheme()?.currentTheme;
  const { formatDate } = useCalendar();
  const { isReadOnly } = useImpersonation();
  const { user } = useAuth();
  const isPrimaryAdmin = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;

  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailId,   setDetailId]   = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const [filters, setFilters] = useState({ month: THIS_MONTH, status: '', category: '', search: '' });
  const [page, setPage]       = useState(1);
  const [dlOpen, setDlOpen]   = useState(false);
  const [dlLoading, setDlLoading] = useState(false);

  const monthToRange = (month) => {
    const [y, m] = month.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate(); // day count only — no toISOString/UTC shift
    return {
      startDate: `${y}-${pad2(m)}-01`,
      endDate  : `${y}-${pad2(m)}-${pad2(lastDay)}`,
    };
  };

  const handleDownload = (format) => {
    const params = {
      format,
      ...monthToRange(filters.month),
      ...(filters.status   ? { status: filters.status }          : {}),
      ...(filters.category ? { category: filters.category }      : {}),
    };
    downloadExpenseReport(params, setDlLoading);
  };

  const { data: summaryData } = useQuery({
    queryKey: ['expense-summary'],
    queryFn:  () => expSvc.getExpenseSummary().then(r => r.data),
  });

  const { data, isPending } = useQuery({
    queryKey: ['expenses', filters, page],
    queryFn:  () => expSvc.getExpenses({ ...filters, page, limit: 30 }).then(r => r.data),
  });

  const expenses = data?.expenses || [];
  const total    = data?.total    || 0;
  const pages    = data?.pages    || 1;

  useAutoRefresh([['expense-summary'], ['expenses', filters, page]], 20000);

  const approveMutation = useMutation({
    mutationFn: (id) => expSvc.approveExpense(id),
    onSuccess:  () => {
      toast.success('Expense approved');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-summary'] });
      qc.invalidateQueries({ queryKey: ['expense-detail'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const archiveMutation = useMutation({
    mutationFn: (id) => expSvc.deleteExpense(id),
    onSuccess:  () => {
      toast.success('Expense archived');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-summary'] });
      setDetailId(null);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to archive'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => expSvc.cancelExpense(id),
    onSuccess:  () => {
      toast.success('Expense cancelled');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-summary'] });
      qc.invalidateQueries({ queryKey: ['expense-detail'] });
      setDetailId(null);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to cancel expense'),
  });

  const stopRecurringMutation = useMutation({
    mutationFn: (id) => expSvc.stopRecurringExpense(id),
    onSuccess:  () => {
      toast.success('Recurring stopped. Future months will not auto-generate.');
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-detail'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to stop recurring'),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['expenses'] });
    qc.invalidateQueries({ queryKey: ['expense-summary'] });
    qc.invalidateQueries({ queryKey: ['expense-detail'] });
    setShowForm(false);
    setEditTarget(null);
  };

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div />
        <div className="flex items-center gap-2">
          {/* Download Report dropdown */}
          <div className="relative">
            <button
              onClick={() => setDlOpen(o => !o)}
              disabled={dlLoading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50"
            >
              <MdDownload className="text-base" />
              {dlLoading ? 'Generating…' : 'Download'}
            </button>
            {dlOpen && (
              <>
              <div className="fixed inset-0 z-40" onClick={() => setDlOpen(false)} />
              <div
                className="absolute right-0 mt-1 w-44 bg-card border border-divider rounded-xl shadow-lg z-50 py-1"
                onMouseLeave={() => setDlOpen(false)}
              >
                <button
                  onClick={() => { handleDownload('pdf'); setDlOpen(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover transition-colors"
                >
                  <MdDownload className="text-primary" /> Download PDF
                </button>
                <button
                  onClick={() => { handleDownload('csv'); setDlOpen(false); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover transition-colors"
                >
                  <MdTableChart className="text-primary" /> Export CSV
                </button>
              </div>
              </>
            )}
          </div>
          <button
            onClick={() => !isReadOnly && setShowForm(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}>
            <MdAdd /> Add Expense
          </button>
        </div>
      </div>

      <SummaryCards summary={summaryData?.summary} />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-4">
        <div className="w-full sm:w-auto">
          <DualDatePicker monthOnly value={filters.month} onChange={v => setFilter('month', v)} />
        </div>
        <FilterSelect
          className="w-full sm:w-44"
          value={filters.status}
          onChange={v => setFilter('status', v)}
          options={STATUS_OPTIONS}
        />
        <FilterSelect
          className="w-full sm:w-48"
          value={filters.category}
          onChange={v => setFilter('category', v)}
          options={[{ value: '', label: 'All Categories' }, ...CATEGORIES]}
        />
        <div className="h-10 flex items-center gap-2 w-full sm:flex-1 sm:min-w-[180px] px-3 rounded-lg border border-divider bg-card">
          <MdSearch className="text-muted flex-shrink-0" />
          <input
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
            placeholder="Search title, vendor..."
            className="flex-1 min-w-0 text-sm text-secondary bg-transparent outline-none placeholder:text-subtle"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-divider shadow-sm overflow-hidden">
        {isPending ? (
          <div className="p-8 space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-12 bg-nav-hover animate-pulse rounded-xl" />)}
          </div>
        ) : expenses.length === 0 ? (
          <div className="py-16 text-center">
            <MdReceiptLong className="mx-auto text-4xl text-subtle mb-3" />
            <p className="text-sm font-medium text-muted">No expenses found</p>
            <p className="text-xs text-muted mt-1">Try changing your filters or add a new expense</p>
          </div>
        ) : (
          <>
          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-divider">
            {expenses.map(exp => (
              <div
                key={exp._id}
                onClick={() => setDetailId(exp._id)}
                className="p-4 cursor-pointer active:bg-nav-hover transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary leading-tight">{exp.title}</p>
                    {exp.vendorName && <p className="text-xs text-muted mt-0.5">{exp.vendorName}</p>}
                    <p className="text-xs text-muted mt-0.5">{catLabel(exp.category)}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-base font-bold text-secondary">{fmtNPR(exp.amount)}</p>
                    <p className="text-xs text-muted mt-0.5"><DualDate date={exp.expenseDate} /></p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <StatusBadge status={exp.status} />
                  <RecurringBadge expense={exp} />
                  {exp.submittedBy?.name && (
                    <span className="text-xs text-muted">{exp.submittedBy.name}</span>
                  )}
                  {exp.status === 'pending' && (exp.attachments?.length || 0) === 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">
                      No receipt
                    </span>
                  )}
                </div>
                {exp.status === 'pending' && (
                  <div className="flex gap-2 mt-3" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => !isReadOnly && approveMutation.mutate(exp._id)}
                      disabled={isReadOnly}
                      className="flex items-center gap-1 text-xs font-semibold text-white bg-green-600 px-3 py-1.5 rounded-xl disabled:opacity-40"
                    >
                      <MdCheckCircle className="text-sm" /> Approve
                    </button>
                    <button
                      onClick={() => !isReadOnly && setRejectTarget(exp)}
                      disabled={isReadOnly}
                      className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-500/15 px-3 py-1.5 rounded-xl disabled:opacity-40"
                    >
                      <MdCancel className="text-sm" /> Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-divider">
                  {['Date', 'Title', 'Category', 'Amount', 'Method', 'Submitted By', 'Status', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp._id}
                    className="border-b border-divider last:border-0 hover:bg-nav-hover cursor-pointer transition-colors"
                    onClick={() => setDetailId(exp._id)}>
                    <td className="px-4 py-3 text-muted whitespace-nowrap"><DualDate date={exp.expenseDate} /></td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-secondary">{exp.title}</p>
                      {exp.vendorName && <p className="text-xs text-muted mt-0.5">{exp.vendorName}</p>}
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {exp.status === 'pending' && (exp.attachments?.length || 0) === 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">
                            <MdWarning className="text-[11px]" /> No receipt
                          </span>
                        )}
                        <RecurringBadge expense={exp} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted">{catLabel(exp.category)}</td>
                    <td className="px-4 py-3 font-semibold text-secondary whitespace-nowrap">{fmtNPR(exp.amount)}</td>
                    <td className="px-4 py-3 text-muted hidden md:table-cell">{methodLabel(exp.paymentMethod)}</td>
                    <td className="px-4 py-3 text-muted hidden lg:table-cell">
                      {exp.submittedBy?.name || '—'}
                      {exp.submittedByRole === 'trainer' && (
                        <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">Trainer</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={exp.status} /></td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      {exp.status === 'pending' && (
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => !isReadOnly && approveMutation.mutate(exp._id)}
                            disabled={isReadOnly}
                            className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={isReadOnly ? 'Disabled: read-only support mode' : 'Approve'}>
                            <MdCheckCircle />
                          </button>
                          <button
                            onClick={() => !isReadOnly && setRejectTarget(exp)}
                            disabled={isReadOnly}
                            className="p-1.5 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={isReadOnly ? 'Disabled: read-only support mode' : 'Reject'}>
                            <MdCancel />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-divider">
            <p className="text-xs text-muted">{total} total expenses</p>
            <div className="flex gap-1.5">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="inline-flex items-center justify-center min-h-[40px] px-4 rounded-lg text-xs font-medium border border-divider disabled:opacity-40 hover:bg-nav-hover transition-colors">
                Previous
              </button>
              <span className="px-3 py-1.5 text-xs text-muted">{page} / {pages}</span>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                className="inline-flex items-center justify-center min-h-[40px] px-4 rounded-lg text-xs font-medium border border-divider disabled:opacity-40 hover:bg-nav-hover transition-colors">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {(showForm || editTarget) && (
        <ExpenseFormModal
          expense={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSaved={invalidateAll}
        />
      )}

      {detailId && (
        <ExpenseDetail
          id={detailId}
          isAdmin={true}
          isPrimaryAdmin={isPrimaryAdmin}
          onClose={() => setDetailId(null)}
          onApprove={(exp) => { approveMutation.mutate(exp._id); setDetailId(null); }}
          onReject={(exp) => { setDetailId(null); setRejectTarget(exp); }}
          onEdit={(exp) => { setDetailId(null); setEditTarget(exp); }}
          onArchive={(exp) => archiveMutation.mutate(exp._id)}
          onCancel={(exp) => cancelMutation.mutate(exp._id)}
          onStopRecurring={(exp) => stopRecurringMutation.mutate(exp._id)}
        />
      )}

      {rejectTarget && (
        <RejectModal
          expense={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onRejected={() => {
            setRejectTarget(null);
            qc.invalidateQueries({ queryKey: ['expenses'] });
            qc.invalidateQueries({ queryKey: ['expense-summary'] });
          }}
        />
      )}
    </div>
  );
}

// ── Main Admin Expenses Page (backward compatibility) ──────────────────────────
export default function Expenses() {
  document.title = 'Expenses — TrainAxis';
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary flex items-center gap-2">
          <MdReceiptLong className="text-primary" /> Expenses
        </h1>
        <p className="text-sm text-muted mt-0.5">Track and manage all club expenses</p>
        <p className="text-xs text-muted mt-1 flex items-center gap-1 opacity-70">
          <MdInfoOutline className="text-amber-500 flex-shrink-0" />
          Trainer expenses appear as <strong>Awaiting Admin Approval</strong> until you approve or reject them
        </p>
      </div>
      <ExpensesPanel />
    </div>
  );
}
